import { persistWorkspaceId, clearWorkspaceId } from '@/lib/tenant/workspace-id-storage';
import { reconcileAppThemeOwner } from '@/lib/theme/app-themes/app-theme-cookie';
import { persistAccessToken, clearAccessToken } from '@/lib/tenant/access-token-storage';
import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { authApi } from '@/lib/api/auth';
import { clearPlaySession, getPlayUser } from '@/lib/phishtrainer/play-api';
import { hardNavigate } from '@/lib/auth/hard-navigate';
import { sessionEndedLoginHref } from '@/lib/status/session-ended';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  companyId: string;
  companyName: string;
  avatarUrl?: string | null;
  /**
   * True when the account was provisioned with a one-time temporary password
   * (admin-created agency owner). The app forces a password reset before any
   * other action while this is set.
   */
  mustChangePassword?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  workspaceId: string | null;
  domainId: string | null;
  isAuthenticated: boolean;

  login: (data: {
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
    workspaceId?: string;
    /**
     * Report this browser's zone onto the signed-in user (default true).
     * False for an impersonation session: its token's subject is the TARGET
     * user, so the report would store the super admin's zone on the customer
     * (TIME-25). The API refuses it during impersonation too.
     */
    reportTimezone?: boolean;
  }) => void;
  /**
   * Sign this browser out and go to the sign-in page. No argument is the
   * person's own "Sign out": a bare /login. 'session-ended' is a session that
   * ended under them (a token the API refuses): /login?next=<this page>, so the
   * sign-in page says so and returns them here (sessionEndedLoginHref).
   */
  logout: (reason?: 'session-ended') => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setWorkspace: (workspaceId: string) => void;
  setDomain: (domainId: string | null) => void;
  setUser: (user: AuthUser) => void;
}

/**
 * SESSION_COOKIE_MAX_AGE and the token writes themselves now live in
 * `lib/tenant/access-token-storage.ts`, beside their workspace-cookie sibling.
 * This file used to own the constant and hand-roll the `document.cookie` write
 * at three call sites — which is how the FOURTH writer (`tryRefreshToken` in
 * lib/api/client.ts) got away with updating localStorage only, and the
 * server-side half of every admin page went stale 24h into a live session.
 * One writer, one lifetime, both channels.
 */

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        accessToken: null,
        refreshToken: null,
        workspaceId: null,
        domainId: null,
        isAuthenticated: false,

        login: ({ user, accessToken, refreshToken, workspaceId, reportTimezone = true }) => {
          // Persist to localStorage for the fetchApi client to read
          if (typeof window !== 'undefined') {
            // Writes localStorage AND the cookie every route handler reads.
            persistAccessToken(accessToken);
            localStorage.setItem('hf-app:refresh-token', refreshToken);
            // TOTAL, not `if (workspaceId)`. A login that carries no workspace
            // used to leave whatever the previous operator's session had put in
            // the cookie: on a shared browser, A's session 401s out, B signs in
            // and `/v1/workspaces/my` flakes (a documented flake — see
            // team-switcher.tsx), and B's server-side calls then went out with
            // A's workspace id. B either got 401 "User does not belong to this
            // workspace" on every server-rendered page, or — if B is also a
            // member of A's workspace — silently read the wrong tenant while
            // the sidebar showed the right one. No workspace means NO workspace
            // cookie, never a stale one.
            if (workspaceId) {
              persistWorkspaceId(workspaceId);
            } else {
              clearWorkspaceId();
            }
            // Note: hf-app:onboarding-complete is set only after onboarding wizard completes
            // (in onboarding/page.tsx handleComplete). Middleware falls back to backend check
            // if this cookie is absent, so existing users are unaffected after first visit.

            // #277 defence in depth: if this browser has a cached PhishTrainer
            // "Play as this user" session bound to a DIFFERENT admin than the
            // one now logging in, drop it. logout() already clears hf-play:*
            // unconditionally (below), so this only matters when logout was
            // skipped (browser closed / crashed without signing out) — admin B
            // must never inherit admin A's impersonated learner session. A
            // genuine learner's own session (no impersonatedByAdminId) is left
            // alone — it has nothing to do with which admin is signed in.
            const stalePlayUser = getPlayUser();
            if (
              stalePlayUser?.impersonatedByAdminId &&
              stalePlayUser.impersonatedByAdminId !== user.id
            ) {
              clearPlaySession();
            }

            // The ready-made theme mirrored for the first paint names its owner
            // (hf-app-theme=<theme>.<userId>). On a shared browser, drop the
            // previous person's, so this person's first app page is not painted
            // in it; their own arrives from their account
            // (lib/theme/app-themes/app-theme-cookie.ts).
            reconcileAppThemeOwner(user.id);
          }

          set(
            {
              user,
              accessToken,
              refreshToken,
              workspaceId: workspaceId ?? null,
              isAuthenticated: true,
            },
            false,
            'auth/login',
          );

          // Best-effort: record the browser timezone so security emails render in
          // the user's own zone. This is the single funnel for EVERY login method
          // (SSO, magic-link, password, OTP, 2FA), so redirect flows are covered
          // and the stored zone refreshes on each login. Fire-and-forget.
          // Skipped when the caller opts out (impersonation — see the type).
          if (typeof window !== 'undefined' && reportTimezone) {
            void authApi.reportTimezone();
          }
        },

        logout: (reason) => {
          // Clear localStorage
          if (typeof window !== 'undefined') {
            clearAccessToken();
            localStorage.removeItem('hf-app:refresh-token');
            clearWorkspaceId();
            // Drop any in-flight sync banner — its store persists, so a stale
            // domainId would otherwise survive logout and make the global
            // SyncBackgroundBanner poll /v1/onboarding/sync-progress from the
            // /login page (401-loop + "syncing…" flash over the login form).
            localStorage.removeItem('hf-app:sync-banner');
            // The AI chat's open conversation (lib/ai-chat/chat-context.tsx
            // persists it so a refresh reopens it) belongs to this session,
            // not to the browser: the next person signing in starts clean.
            localStorage.removeItem('ai-chat-active-id');
            // Clear auth + onboarding cookies so middleware gates trigger on next visit
            // (the access-token cookie is cleared by clearAccessToken above)
            document.cookie = 'hf-app:refresh-token=; path=/; max-age=0';
            document.cookie = 'hf-app:onboarding-complete=; path=/; max-age=0';

            // #277 P1: admin logout must ALSO end any PhishTrainer play
            // session in this browser (hf-play:*). Before this fix, only
            // three call sites ever cleared it (the learner's own sign-out, a
            // 401/403, and impersonating a DIFFERENT user) — admin logout was
            // not one of them. The residual token is a legitimately minted
            // learner JWT, but /play requires no admin auth, so it outlived
            // the admin session that (in the impersonation case) created it:
            // admin A impersonates a learner, logs out; admin B logs in on
            // the same browser, clicks Play, and lands in A's learner
            // session. Clearing unconditionally on every admin logout is the
            // safe default for a shared machine — no authenticated session,
            // admin or learner, should survive an explicit sign-out.
            clearPlaySession();
          }

          set(
            {
              user: null,
              accessToken: null,
              refreshToken: null,
              workspaceId: null,
              domainId: null,
              isAuthenticated: false,
            },
            false,
            'auth/logout',
          );

          // Redirect to login — carrying the page only when the session ended
          // under the person; their own "Sign out" never does.
          if (typeof window !== 'undefined') {
            hardNavigate(
              reason === 'session-ended' ? sessionEndedLoginHref(window.location) : '/login',
            );
          }
        },

        setTokens: (accessToken, refreshToken) => {
          if (typeof window !== 'undefined') {
            // Keep both channels in sync with the refreshed token.
            persistAccessToken(accessToken);
            localStorage.setItem('hf-app:refresh-token', refreshToken);
            // Re-arm the workspace cookie on the same schedule. It carried the
            // same 24h max-age as the token cookie but nothing ever refreshed
            // it, so in a session that outlives a day the server side silently
            // lost its workspace and fell back to the JWT's first-membership
            // claim — the exact pin this was supposed to remove.
            const ws = localStorage.getItem('hf-app:workspace-id');
            if (ws) persistWorkspaceId(ws);
          }

          set({ accessToken, refreshToken }, false, 'auth/setTokens');
        },

        setWorkspace: (workspaceId) => {
          // Writes BOTH channels (localStorage + the cookie the server reads).
          //
          // This used to claim it "runs on every hydration, so it is also what
          // repairs a cookie left stale". It does not, and never did:
          // AuthHydrationProvider's /auth/me effect returns early whenever the
          // store already holds a persisted user — every returning session — so
          // setWorkspace is reached only on a genuine cold hydration or a
          // TeamSwitcher switch. The repair now lives in its own ungated effect
          // in components/providers/auth-hydration-provider.tsx; do not put a
          // repair back on a path gated by the absence of a persisted user.
          //
          // 'all' is the company-wide VIEW sentinel, not a workspace id.
          // workspace-store.setActiveWorkspace already skips it; this call site
          // did not, and the literal string would have gone out as
          // `x-workspace-id: all`, which JwtStrategy 403s on (not a UUID) —
          // every server-side admin call failing at once. Not reachable today
          // (the only caller passes a real membership id), guarded so the next
          // caller cannot make it reachable.
          if (workspaceId !== 'all') persistWorkspaceId(workspaceId);

          set({ workspaceId }, false, 'auth/setWorkspace');
        },

        setDomain: (domainId) => {
          set({ domainId }, false, 'auth/setDomain');
        },

        setUser: (user) => {
          set({ user }, false, 'auth/setUser');
        },
      }),
      {
        name: 'hf-app:auth-store',
        partialize: (state) => ({
          user: state.user,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          workspaceId: state.workspaceId,
          domainId: state.domainId,
          isAuthenticated: state.isAuthenticated,
        }),
        /**
         * Re-arm the access-token COOKIE from the rehydrated session, on every
         * page load.
         *
         * THE HOLE THIS FILLS. `hf-app:access-token` exists in two places:
         * localStorage, which `fetchApi` reads, and this cookie, the only half a
         * Next route handler or server component can read (`adminAuth()` in
         * lib/phishtrainer/backend.ts, the LMS SCORM routes). Only `login()` and
         * `setTokens()` ever wrote the cookie — and `setTokens` has ONE caller,
         * AuthHydrationProvider's /auth/me effect, which returns early whenever
         * the store already holds a persisted user. zustand's `persist` rehydrates
         * synchronously from localStorage, so `user` is set before that effect
         * runs and setTokens is unreachable for every returning session.
         * Meanwhile `tryRefreshToken()` (lib/api/client.ts) writes the rotated
         * token to localStorage ONLY. So the client half was renewed every 8h and
         * the cookie half decayed: at the 24h mark adminAuth() started returning
         * null mid-session and every server-side admin proxy fell through to its
         * empty fallback, while the client-side half of the same page kept
         * working — the confusing half-broken screen this closes.
         *
         * Rehydration is the right hook precisely because it is UNGATED: it fires
         * on every load that has a stored session, which is the only event the
         * cookie's lifetime can be pinned to. (Its sibling — the workspace cookie
         * — is re-armed on the same schedule by an ungated effect in
         * components/providers/auth-hydration-provider.tsx. The two must stay on
         * the same schedule; see SESSION_COOKIE_MAX_AGE.)
         *
         * Not a way in for an unauthenticated visitor: with no persisted session
         * there is no token here and nothing is written.
         */
        onRehydrateStorage: () => (state) => {
          if (typeof window === 'undefined' || !state) return;
          // localStorage FIRST: it is the channel tryRefreshToken() rotates,
          // while this persisted blob is written only by login()/setTokens()
          // and goes stale after the first refresh. Re-arming from the blob
          // put the dead login-time token back into BOTH channels on every
          // load — and, localStorage being shared, into every other open tab
          // (2026-09-10 review). The blob is the fallback, not the source.
          let token: string | null = null;
          try {
            token = localStorage.getItem('hf-app:access-token');
          } catch {
            // Private mode / site data blocked — fall back to the blob.
          }
          if (!token) token = state.accessToken;
          if (!token) return;
          // Same single writer as every other token write — re-arming the
          // cookie by hand here is what let the four writers drift in the
          // first place.
          persistAccessToken(token);
        },
      },
    ),
    { name: 'AuthStore' },
  ),
);
